import React, { useEffect, useRef } from "react";
import ConnectableObject from "../models/ConnectableObject";
import Connection, { ConnectionType } from "../models/Connection";

import Page from "../models/Page";
import Point from "../models/Point";
import {
  eventPointToDrawingArea,
  getCenterOffsetFromStyle,
  getDrawingAreaElement,
  getLeftTopFromStyle,
  setLeftTopInStyle,
} from "../modelViews/GraficElementUtils";

import Logger from "../Logger";
import Browser from "../browser";
import type Room from "../models/Room";
import { RenderTarget, useRenderTarget } from "./renderPrimitives/renderTarget";

const logger = new Logger();
var dxy = { x: 0, y: 0 };
var currentGrabElement: any | undefined = undefined;

export class GrabMoveHandler {
  thisElement: any;
  parent: any;
  subElement: any;

  onMouseDown: any;
  onClick: any;
  onMoveFinished: any;
  onMove: any;
  onCancel: any;
  onMoveStarted: any;

  onMoveStartedCalled = false;

  pStart = new Point(0, 0);

  orgCursor = "";
  currentPointerId: number | null = null;

  constructor(thisElement: React.RefObject<HTMLDivElement>) {
    this.thisElement = thisElement;
    if (thisElement.current) {
      this.parent = getDrawingAreaElement(thisElement.current);
    } else {
      logger.error("thisElement.current is undefined");
      return;
    }

    const xElm = this.thisElement.current;

    xElm.addEventListener("pointerdown", this.onPointerDownHandler);
    xElm.addEventListener("pointermove", this.onPointerMoveHandler);
    xElm.addEventListener("pointerup", this.onPointerUpHandler);
    xElm.addEventListener("pointercancel", this.onPointerCancelHandler);
    xElm.addEventListener("click", this.onClickHandler);
    xElm.addEventListener("dblclick", this.onDblClickHandler);

    //////////////////////////////////////////////////////////////////////////////////
  }

  release = () => {
    if (this.thisElement.current) {
      const xElm = this.thisElement.current;

      xElm.removeEventListener("pointerdown", this.onPointerDownHandler);
      xElm.removeEventListener("pointermove", this.onPointerMoveHandler);
      xElm.removeEventListener("pointerup", this.onPointerUpHandler);
      xElm.removeEventListener("pointercancel", this.onPointerCancelHandler);
      xElm.removeEventListener("click", this.onClickHandler);
      xElm.removeEventListener("dblclick", this.onDblClickHandler);
    }
  };

  getCenter = (): Point => {
    if (this.subElement) {
      const r1 = this.thisElement.current.getBoundingClientRect();
      const r2 = this.thisElement.current.getBoundingClientRect();
      const p = new Point(r2.left - r1.left + r2.width / 2, r2.top - r1.top + r2.height / 2);

      return p;
    } else {
      if (currentGrabElement) {
        return getCenterOffsetFromStyle(currentGrabElement);
      } else {
        return new Point(0, 0);
      }
    }
  };

  avoidRecursionFlags = false;

  lastConnectionTarget: HTMLElement | undefined;
  lastTouchX = 0;
  lastTouchY = 0;

  forewardTouchEvent(connectionTarget: HTMLElement, name: string, event: any) {
    try {
      if (this.avoidRecursionFlags) {
        logger.log("recursion detected.... SKIP");
      } else {
        const myEvent = new MouseEvent(name, {
          bubbles: true,
          cancelable: true,
          composed: false,
          clientX: this.lastTouchX,
          clientY: this.lastTouchY,
          buttons: 1,
          button: 1,
        });
        this.avoidRecursionFlags = true;
        connectionTarget.dispatchEvent(myEvent);
        this.avoidRecursionFlags = false;
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  onPointerMoveHandler = (event: PointerEvent) => {
    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    if (currentGrabElement) {
      event.preventDefault();
      event.stopPropagation();

      const p = {
        x: event.clientX,
        y: event.clientY,
      };

      const delta = Math.max(Math.abs(p.x - this.pStart.x), Math.abs(p.y - this.pStart.y));

      if (p.y) {
        p.y -= Browser.yOffsetTouch();
      }

      let pLocal = eventPointToDrawingArea(event, p.x, p.y);

      if (delta > 10 && pLocal && event.pressure > 0) {
        if (!this.onMoveStartedCalled && this.onMoveStarted) {
          this.onMoveStartedCalled = true;
          this.onMoveStarted(pLocal);
        }

        if (this.onMove) {
          this.onMove(pLocal);
        } else {
          const center = this.getCenter();

          const p1 = new Point(pLocal.x - center.x - dxy.x, pLocal.y - center.y - dxy.y);
          setLeftTopInStyle(currentGrabElement, p1);
        }

        // Handle connection targets for pointer events
        this.lastTouchX = p.x;
        this.lastTouchY = p.y;

        const target = document.elementFromPoint(this.lastTouchX, this.lastTouchY);

        if (this.lastConnectionTarget) {
          this.lastConnectionTarget.classList.remove("hoverTarget"); // no itx
        }

        console.log("target: ", target?.id || "undefined");

        if (
          target &&
          target.id != "mainDrawingArea" &&
          target.id != "pageShadowWrapper" &&
          !target.id.endsWith("_p") &&
          !target.id.startsWith("triangle")
        ) {
          let connectionTarget = target.closest(".connectionTarget") as HTMLElement;

          if (!connectionTarget) {
            connectionTarget = target.querySelector(".connectionTarget") as HTMLElement;
          }

          if (connectionTarget) {
            this.lastConnectionTarget = connectionTarget;
            if (!connectionTarget.contains(event.currentTarget as Node)) {
              connectionTarget.classList.add("hoverTarget"); // no itx
              this.forewardTouchEvent(connectionTarget, "mousemove", event);
              return;
            }
          } else {
          }
        }

        if (state.currentDrawingConnection.connection) {
          state.currentDrawingConnection.connection.toDevice = undefined;
        }
        this.lastConnectionTarget = undefined;
      }
    }
  };

  onClickHandler = (event: MouseEvent) => {
    event.stopPropagation();
    console.log("onClick");
    if (this.onClick) {
      this.onClick(event);
    }
  };

  onDblClickHandler = (event) => {
    event.stopPropagation();
    // console.log("onDoubleClick")
    // if (this.onDblClick) {
    //   this.onDblClick(event);
    // }
  };

  onPointerDownHandler = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    currentGrabElement = this.thisElement.current;
    this.currentPointerId = event.pointerId;

    // Pointer capturing
    if (this.thisElement.current) {
      this.thisElement.current.setPointerCapture(event.pointerId);
    }

    this.pStart = new Point(event.clientX, event.clientY);

    if (currentGrabElement) {
      const pMouseDownInDrawingSpace = eventPointToDrawingArea(event);
      if (pMouseDownInDrawingSpace) {
        const leftTop = getLeftTopFromStyle(currentGrabElement);
        const center = this.getCenter();

        dxy = {
          x: pMouseDownInDrawingSpace.x - leftTop.x - center.x,
          y: pMouseDownInDrawingSpace.y - leftTop.y - center.y,
        };

        this.onMoveStartedCalled = false;
        if (this.onMouseDown) {
          this.onMouseDown(pMouseDownInDrawingSpace);
        }
      }
    }
  };

  onPointerUpHandler = (event: PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    if (currentGrabElement) {
      // Release pointer capture
      if (this.thisElement.current) {
        try {
          this.thisElement.current.releasePointerCapture(event.pointerId);
        } catch (e) {
          // Ignore errors if pointer was already released
        }
      }

      if (this.lastConnectionTarget) {
        this.forewardTouchEvent(this.lastConnectionTarget, "mouseup", event);
      }

      if (this.onMoveFinished) {
        this.onMoveFinished(getLeftTopFromStyle(currentGrabElement));
      }

      if (this.lastConnectionTarget) {
        this.lastConnectionTarget.classList.remove("hoverTarget"); // no itx
      }

      if (!this.onMoveStartedCalled) {
        if (this.onClick) {
          this.onClick();
        }
      }

      this.currentPointerId = null;
      currentGrabElement = null;
    }
  };

  onPointerCancelHandler = (event: PointerEvent) => {
    // Only handle the pointer that started the drag
    if (this.currentPointerId && event.pointerId !== this.currentPointerId) {
      return;
    }

    // Release pointer capture
    if (this.thisElement.current) {
      try {
        this.thisElement.current.releasePointerCapture(event.pointerId);
      } catch (e) {
        // Ignore errors if pointer was already released
      }
    }

    this.currentPointerId = null;
    currentGrabElement = null;

    if (this.onCancel) {
      this.onCancel();
    }
  };

  // Legacy methods - keeping for backward compatibility but redirecting to pointer events
  onMouseDownHandler = (event: any) => {
    const pointerEvent = event as PointerEvent;
    this.onPointerDownHandler(pointerEvent);
  };
}

export default function ConnectorGrab(props: {
  connectableObject: ConnectableObject;
  mainPage: Page;
  page: Page;
  portName: string;
  x: number;
  ownerSelected: boolean;
  direction?: number;
  type: ConnectionType;
  style?: any;
  size?: number; // in cm
  disabled?: boolean;
  onClick?: () => void;
}) {
  const { connectableObject, mainPage, page, x, portName, ownerSelected, direction, type, size, disabled, onClick } =
    props;

  var { style: outerStyle } = props;

  const renderTarget = useRenderTarget();
  const forPdf = renderTarget === RenderTarget.pdf;
  if (forPdf) {
    return null;
  }

  const thisElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!disabled) {
      var xmoveHandler = new GrabMoveHandler(thisElement);
      xmoveHandler.onMouseDown = () => {
        logger.log("onMouseDown ConnectionGrab");
      };

      xmoveHandler.onMoveStarted = (p: Point) => {
        if (state.currentDrawingConnection.connection == null) {
          let page1: Page | undefined = undefined;
          if (connectableObject.isScene || connectableObject.isButtonChannel || connectableObject.isChannel) {
            page1 = mainPage;
          } else {
            const startRoom = connectableObject.parent as Room;
            page1 = startRoom.isFloorGeneralRoom ? mainPage.parentFloor?.page : startRoom.page;
          }

          if (page1) {
            const c = new Connection({}, page1);
            c.isHighlighted = true;
            c.fromId = connectableObject.id;

            //
            // MWE 6.8.2025: das funktioniert so nicht. DAs ist etwas komplizierter....
            // für jetzt nehmen wir den Punkt, an dem der Grab gestartet wurde
            //
            // let p0 = eventPointToDrawingArea({ currentTarget: thisElement.current }, 1, 1);
            //

            // c.tmpFromPoint = p;

            c.type = type || ConnectionType.FromSourceDevice;
            page1.connections.add(c);
            c.updateEndPoints();
            state.currentDrawingConnection.channelType = connectableObject.channelType;
            state.currentDrawingConnection.connection = c;
            state.currentDrawingConnection.page = page1;
          }
        }
      };

      xmoveHandler.onMove = (p: Point) => {
        if (state.currentDrawingConnection.connection) {
          logger.log(`onMove ConnectionGrab ${state.currentDrawingConnection.connection.toDevice == undefined}`);
          if (state.currentDrawingConnection.connection.toDevice == undefined) {
            state.currentDrawingConnection.connection.tmpToPoint = p;

            state.currentDrawingConnection.connection.animateFromPoint = undefined;
            state.currentDrawingConnection.connection.animateStartTime = undefined;

            state.currentDrawingConnection.connection.updateEndPoints();
            state.currentDrawingConnection.connection.forbidden = false;
            state.currentDrawingConnection.connection.type = ConnectionType.Unknown;
            state.currentDrawingConnection.connection.tmpType = undefined;
            state.currentDrawingConnection.connection.raiseOnChange();
          }
        }
      };

      xmoveHandler.onCancel = () => {
        if (state.currentDrawingConnection.connection) {
          page.deleteConnection(state.currentDrawingConnection.connection);

          state.currentDrawingConnection.connection = null;
          state.currentDrawingConnection.page = null;
        }
      };

      xmoveHandler.onMoveFinished = (p: Point) => {
        if (state.currentDrawingConnection.connection) {
          page.deleteConnection(state.currentDrawingConnection.connection);

          state.currentDrawingConnection.connection = null;
          state.currentDrawingConnection.page = null;
        }
      };

      xmoveHandler.onClick = () => {
        //
        // foreward to parent (Consumer etc.)
        //
        if (onClick) {
          onClick();
        }
      };

      return () => {
        xmoveHandler.release();
      };
    }
  });

  var style: any = {
    position: "absolute",
    top: "calc(50% - 3px)", // no itx
    pointerEvents: "auto",
    marginLeft: `0xp`,
    backgroundColor: "transparent",
  };

  if (outerStyle == undefined) {
    outerStyle = {
      position: "absolute",
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: "20",
    };
  }

  if (x <= 0) {
    style.marginLeft = `calc(${x}% - var(--connectionGrabHoverBorderRadius) + 2px)`;
  } else {
    style.marginLeft = `calc(${x}% - var(--connectionGrabHoverBorderRadius) + 2px)`;

    //style.marginLeft = "auto";
  }

  const dxSelect = 10;
  const dySelect = 7;
  const selectBorderStyle: any = {
    position: "absolute",
    left: `0px`,
    top: `${-dySelect}px`,
    width: `calc(100% + ${2 * dxSelect}px)`,
    height: `calc(100% + ${2 * dySelect}px)`,
    // backgroundColor: "yellow",
  };

  let classTriangle = "";
  if (direction == 180) {
    style.borderRadius = "100px 0px 0px 100px";
    classTriangle = "triangleLeft";
    selectBorderStyle.left = `-${2 * dxSelect}px`;
  } else {
    style.marginLeft = `calc(100% - 2px)`;
    classTriangle = "triangleRight";
  }

  var sizeStyle = {};
  if (size != undefined) {
    sizeStyle = {
      width: `${size}cm`,
      height: `${size}cm`,
      borderRadius: `${size}cm`,
    };
  }

  return (
    <div className="connectorGrabOuter" style={outerStyle}>
      {!disabled && (
        <div
          className={ownerSelected ? "connectorGrab ownerSelected" : "connectorGrab"} // no itx
          ref={thisElement}
          style={{
            ...sizeStyle,
            ...style,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
          onPointerDown={(e) => {
            console.log("onPointerDown ConnectorGrab", e);
            e.stopPropagation();
            e.preventDefault();
          }}
          onPointerUp={(e) => {
            console.log("onPointerUp ConnectorGrab", e);
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={
            Browser.isTouch()
              ? () => {
                  if (onClick) {
                    onClick();
                  }
                }
              : undefined
          }
        >
          {/* <div className="selectBorder" style={selectBorderStyle}></div> */}
          <div id={classTriangle} className={classTriangle}></div>
        </div>
      )}
    </div>
  );
}
